import React from 'react'
import { motion } from 'framer-motion'
import { Container } from '../components/ui/Container'
import PageLayout from '../components/layout/PageLayout'
import ProblemOpportunitySection from '../components/pitch-deck/ProblemOpportunitySection'
import SevenPillarsSection from '../components/pitch-deck/SevenPillarsSection'
import MarketVisionSection from '../components/pitch-deck/MarketVisionSection'
import BusinessModelSection from '../components/pitch-deck/BusinessModelSection'
import DefensibilityMoatSection from '../components/pitch-deck/DefensibilityMoatSection'
import { Zap, ChevronRight, BarChart3, Users, PieChart } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Link } from 'react-router-dom'

const InvestorPitchDeck: React.FC = () => {
  return (
    <PageLayout>
      {/* Pitch Deck Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-theme-bg via-theme-surface to-theme-bg overflow-hidden relative">
        <Container>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-theme-primary/10 border border-theme-primary/20 text-theme-primary text-xs font-bold uppercase tracking-widest mb-8"
            >
              <Zap className="w-3 h-3" />
              <span>Investment Memorandum Q1 2027</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-7xl font-black text-theme-text-primary mb-8 leading-tight"
            >
              The Operating System <br /> for <span className="gradient-heading">Human Biology.</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-theme-secondary mb-12 leading-relaxed"
            >
              Scaling medical-grade longevity through vertical integration, AI-driven diagnostics, and a lab-to-table ecosystem.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Button size="lg" className="bg-theme-primary text-theme-text-inverse hover:opacity-90 transition-opacity">
                Download Full Deck (PDF)
              </Button>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-theme-border text-theme-text-primary hover:bg-theme-primary/10">
                  Speak with Investor Relations
                </Button>
              </Link>
            </motion.div>
          </div>
        </Container>
        
        {/* Background elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none" />
      </section>

      {/* Key Stats / Teaser Section */}
      <section className="py-20 bg-theme-surface border-y border-theme-border">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-black text-theme-text-primary mb-2">$4.2T</div>
              <div className="text-theme-secondary text-sm uppercase tracking-wider font-bold">Total Addressable Market</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-theme-primary mb-2">94%</div>
              <div className="text-theme-secondary text-sm uppercase tracking-wider font-bold">Cohort Retention</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-theme-text-primary mb-2">12M+</div>
              <div className="text-theme-secondary text-sm uppercase tracking-wider font-bold">Biometric Data Points</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-black text-theme-primary mb-2">3.2x</div>
              <div className="text-theme-secondary text-sm uppercase tracking-wider font-bold">LTV/CAC Ratio</div>
            </div>
          </div>
        </Container>
      </section>

      {/* Slide 1: Problem & Opportunity */}
      <ProblemOpportunitySection />

      {/* Slide 2: The Core Ecosystem (7 Pillars) */}
      <SevenPillarsSection />

      {/* Defensibility Section */}
      <DefensibilityMoatSection />

      {/* Slide 3: The Market Vision */}
      <MarketVisionSection />

      {/* Slide 4: Business Strategy & Economics */}
      <BusinessModelSection />

      {/* Traction / Investment Highlights */}
      <section className="section-padding bg-theme-bg">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold gradient-heading mb-6">Investment Highlights</h2>
            <p className="text-xl text-theme-secondary max-w-2xl mx-auto">Why BIOTUNE is the definitive platform for the longevity economy.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-3xl bg-theme-surface border border-theme-border flex flex-col items-start space-y-4 shadow-lg shadow-theme-primary/5">
              <div className="w-12 h-12 bg-theme-primary/10 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-theme-primary" />
              </div>
              <h3 className="text-xl font-bold text-theme-text-primary">Compound Data Moat</h3>
              <p className="text-theme-secondary text-sm leading-relaxed">Every member provides 100+ biomarkers monthly, creating the world's most dense longitudinal dataset on human performance.</p>
            </div>
            
            <div className="p-8 rounded-3xl bg-theme-surface border border-theme-border flex flex-col items-start space-y-4 shadow-lg shadow-theme-success/5">
              <div className="w-12 h-12 bg-theme-success/10 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-theme-success" />
              </div>
              <h3 className="text-xl font-bold text-theme-text-primary">Network Effects</h3>
              <p className="text-theme-secondary text-sm leading-relaxed">Our partner ecosystem (labs + chef network) scales with minimal CapEx, creating a global fulfillment infrastructure.</p>
            </div>
            
            <div className="p-8 rounded-3xl bg-theme-surface border border-theme-border flex flex-col items-start space-y-4 shadow-lg shadow-theme-warning/5">
              <div className="w-12 h-12 bg-theme-warning/10 rounded-xl flex items-center justify-center">
                <PieChart className="w-6 h-6 text-theme-warning" />
              </div>
              <h3 className="text-xl font-bold text-theme-text-primary">Revenue Diversification</h3>
              <p className="text-theme-secondary text-sm leading-relaxed">Integrated revenue streams from high-margin SaaS, marketplace commissions, and fulfillment services.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-b from-theme-bg to-theme-surface">
        <Container>
          <div className="p-12 md:p-20 rounded-[48px] bg-theme-elevated border border-theme-border text-center relative overflow-hidden shadow-2xl">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black text-theme-text-primary mb-8 leading-tight">Ready to transform an industry?</h2>
              <p className="text-xl text-theme-secondary max-w-2xl mx-auto mb-12 leading-relaxed">
                Join us in building the world's first vertically integrated longevity ecosystem. Schedule a briefing with our executive team today.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-theme-primary text-theme-text-inverse hover:opacity-90 px-10 h-14 rounded-full font-bold shadow-lg shadow-theme-primary/20">
                  Access Investor Portal
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            {/* Background decorative shine */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-theme-primary/5 blur-[100px] rounded-full" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-theme-secondary/5 blur-[100px] rounded-full" />
          </div>
        </Container>
      </section>
    </PageLayout>
  )
}

export default InvestorPitchDeck
